import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { TrendingUp, Server, Settings, BarChart, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { BrandDot } from '../components/brand/BrandMotif';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
};

const solutions = [
  {
    icon: TrendingUp,
    title: 'Revenue Growth Transformation',
    description: 'Organizations must move beyond transactional sales models. SGA helps companies build consultative, insight-driven revenue organizations.',
    items: [
      'Sales capability transformation',
      'Consultative selling frameworks',
      'Customer engagement strategies',
      'Go-to-market optimization',
    ],
    color: '#4DB848',
  },
  {
    icon: Server,
    title: 'Digital Infrastructure Solutions',
    description: 'SGA designs and deploys digital infrastructures that enable scalable and secure business operations.',
    items: [
      'Cloud services',
      'Network architecture',
      'Cybersecurity frameworks',
      'Unified communications systems',
    ],
    color: '#4DB848',
  },
  {
    icon: Settings,
    title: 'Operational Optimization',
    description: 'SGA helps enterprises realign resources and operational structures to enhance performance.',
    items: [
      'Organizational redesign',
      'Resource realignment',
      'Operational process optimization',
      'Workplace transformation',
    ],
    color: '#4DB848',
  },
  {
    icon: BarChart,
    title: 'Governance and Performance Management',
    description: 'Sustainable transformation requires structured oversight. SGA establishes governance mechanisms that provide transparency and accountability.',
    items: [
      'Outcome-based business reviews',
      'Performance dashboards',
      'Executive reporting frameworks',
      'Vendor ecosystem management',
    ],
    color: '#4DB848',
  },
];

export const SolutionsPage = () => {
  return (
    <div data-testid="solutions-page">
      {/* Hero Banner - 1920 × 420px slim banner */}
      <section 
        className="relative flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.3)), url('/assets/banners/Solutions_Banner.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center right',
          minHeight: '420px',
          height: '420px',
        }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 w-full">
          <motion.div 
            className="max-w-lg"
            initial="initial"
            animate="animate"
            variants={{
              animate: { transition: { staggerChildren: 0.1 } }
            }}
          >
            <motion.h1 
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
              className="font-montserrat font-bold text-3xl md:text-4xl text-white mb-4"
            >
              Solutions That Deliver Real Outcomes
            </motion.h1>
            <motion.p 
              variants={fadeInUp}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-inter text-base text-white leading-relaxed"
            >
              Delivering integrated solutions that drive growth, efficiency, 
              and digital maturity.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="font-montserrat font-semibold text-sm uppercase tracking-widest text-[#0087B4] mb-4 block">
              Our Solutions
            </span>
            <h2 className="heading-section text-[#0B1E2D] mb-6">
              Solutions for Enterprise Transformation
            </h2>
            <p className="body-text">
              SGA STRAT delivers solutions that address both strategic and operational 
              challenges faced by modern organizations. Our solutions are designed to 
              create measurable business impact and sustainable competitive advantage.
            </p>
          </div>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-8">
            {solutions.map((solution, index) => (
              <motion.div
                key={solution.title}
                data-testid={`solution-card-${index}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group relative bg-white border border-slate-100 shadow-sm hover:shadow-2xl transition-all duration-500 overflow-hidden"
              >
                {/* Accent bar */}
                <div 
                  className="absolute top-0 left-0 right-0 h-1"
                  style={{ backgroundColor: solution.color }}
                />
                
                <div className="p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <div 
                      className="w-14 h-14 flex-shrink-0 flex items-center justify-center"
                      style={{ backgroundColor: `${solution.color}15` }}
                    >
                      <solution.icon className="w-7 h-7" style={{ color: solution.color }} />
                    </div>
                    <h3 className="font-montserrat font-bold text-xl text-[#0B1E2D] pt-2">
                      {solution.title}
                    </h3>
                  </div>
                  
                  <p className="font-inter text-slate-600 leading-relaxed mb-6">
                    {solution.description}
                  </p>
                  
                  <ul className="space-y-3">
                    {solution.items.map((item, idx) => (
                      <li key={idx} className="flex items-center gap-3">
                        <BrandDot size="sm" />
                        <span className="font-inter text-sm text-slate-500">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Brand motif hover effect */}
                <div className="h-2 flex justify-center items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  {[1.5, 2, 2.5, 3, 2.5, 2, 1.5].map((size, i) => (
                    <span 
                      key={i}
                      className="rounded-full bg-[#4DB848] transition-transform duration-300"
                      style={{ 
                        width: size * 2.5, 
                        height: size * 2.5,
                        transitionDelay: `${i * 30}ms`,
                        transform: 'scale(0)',
                      }}
                    />
                  ))}
                </div>
                <style>{`
                  .group:hover span[class*="rounded-full bg-[#4DB848]"] {
                    transform: scale(1) !important;
                  }
                `}</style>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Our Solutions */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="font-montserrat font-semibold text-sm uppercase tracking-widest text-[#0087B4] mb-4 block">
                The SGA Difference
              </span>
              <h2 className="heading-section text-[#0B1E2D] mb-6">
                Why Our Solutions Work
              </h2>
              <p className="body-text mb-6">
                Our solutions are built on a foundation of strategic thinking, 
                operational excellence, and technology expertise. We don't just 
                implement technology — we transform how organizations operate.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  'Strategy-led approach aligned with business objectives',
                  'End-to-end implementation with measurable outcomes',
                  'Continuous optimization and improvement',
                  'Cross-functional integration across departments',
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="w-5 h-5 rounded-full bg-[#4DB848] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                    <span className="font-inter text-slate-600">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="grid grid-cols-2 gap-4"
            >
              {[
                { value: '100+', label: 'Transformations Delivered' },
                { value: '95%', label: 'Client Satisfaction' },
                { value: '50+', label: 'Enterprise Clients' },
                { value: '15+', label: 'Years Experience' },
              ].map((stat, index) => (
                <div 
                  key={index}
                  className="bg-slate-50 p-6 text-center border-l-4 border-[#0087B4]"
                >
                  <p className="font-montserrat font-bold text-4xl text-[#0087B4] mb-2">
                    {stat.value}
                  </p>
                  <p className="font-inter text-sm text-slate-500">
                    {stat.label}
                  </p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#0B1E2D]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="heading-section text-white mb-6">
              Ready to Transform Your Organization?
            </h2>
            <p className="font-inter text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
              Discover how our solutions can help you achieve your strategic objectives 
              and drive sustainable business growth.
            </p>
            <Button 
              asChild
              data-testid="solutions-cta"
              className="bg-[#4DB848] hover:bg-[#3da03a] text-white rounded-none px-10 py-6 font-montserrat font-bold tracking-wider"
            >
              <Link to="/engage">
                Discuss Your Needs
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
